import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../../lib/api';

// ── Constants ─────────────────────────────────────────────────────────────────
const LABEL_W  = 230;  // px for sticky task name column
const ROW_H    = 58;   // px per task row
const TL_START = 0;    // 12:00 AM in minutes
const TL_END   = 1440; // 12:00 AM next day (full 24h) in minutes

// Each zoom level defines:
//   intervalMins — how many minutes between time-axis labels
//   hourW        — px per hour (controls total timeline width)
const ZOOM_PRESETS = [
  { label: '4h',  intervalMins: 240, hourW: 30  }, // label every 4h, compact
  { label: '2h',  intervalMins: 120, hourW: 55  }, // label every 2h
  { label: '1h',  intervalMins: 60,  hourW: 90  }, // label every 1h
  { label: '30m', intervalMins: 30,  hourW: 120 }, // label every 30min (default)
  { label: '15m', intervalMins: 15,  hourW: 240 }, // label every 15min
  { label: '10m', intervalMins: 10,  hourW: 360 }, // label every 10min
  { label: '5m',  intervalMins: 5,   hourW: 720 }, // label every 5min, most detailed
];
const DEFAULT_ZOOM = ZOOM_PRESETS.findIndex(z => z.label === '30m');

const MONTHS      = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const MONTHS_FULL = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAY_ABR     = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
const DAY_FULL    = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];

function toDateKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

function getMondayOf(date) {
  const d = new Date(date);
  d.setHours(0,0,0,0);
  const day = d.getDay();
  d.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
  return d;
}

function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

function timeToMins(t) {
  if (!t) return 0;
  const [h, m] = t.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

function minsToTime(mins) {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`;
}

function durLabel(mins) {
  if (mins <= 0) return '';
  const h = Math.floor(mins / 60), m = mins % 60;
  if (h && m) return `${h}h ${m}m`;
  if (h)      return `${h}h`;
  return `${m}m`;
}

function fmtDue(iso) {
  if (!iso) return null;
  const s = iso.slice(0, 10);
  const [, mo, dd] = s.split('-');
  const mn = parseInt(mo, 10) - 1;
  return `${MONTHS[mn]} ${parseInt(dd, 10)}`;
}

function uid() {
  return `${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
}

// ── Day Timeline — horizontal Gantt-style ─────────────────────────────────────
function DayTimeline({ dateKey, todayKey, dayBlocks, visibleTasks, onRemove, onEdit, onOpenPopup, appId, hourW, intervalMins, onZoomIn, onZoomOut, canZoomIn, canZoomOut, me }) {
  const navigate = useNavigate();
  const outerRef = useRef(null);
  const nowDate  = new Date();
  const nowMins  = nowDate.getHours() * 60 + nowDate.getMinutes();
  const isToday  = dateKey === todayKey;
  const TL_WIDTH = ((TL_END - TL_START) / 60) * hourW;

  // Time-axis ticks at the current zoom interval (30m / 1h / 2h / 4h)
  const axisTicks = [];
  for (let m = TL_START; m < TL_END; m += intervalMins) axisTicks.push(m);

  // Sub-interval grid lines (half of intervalMins) — skip when interval is 30m
  const subTicks = [];
  if (intervalMins > 30) {
    const sub = intervalMins / 2;
    for (let m = TL_START + sub; m < TL_END; m += intervalMins) subTicks.push(m);
  }

  // Auto-scroll so the view starts 1h before the current time (or 9 AM on other days)
  useEffect(() => {
    if (!outerRef.current) return;
    const targetMins = isToday ? Math.max(TL_START, nowMins - 60) : 540;
    outerRef.current.scrollLeft = Math.max(0, ((targetMins - TL_START) / 60) * hourW);
  }, [dateKey, hourW]); // eslint-disable-line react-hooks/exhaustive-deps

  // Click on timeline row → compute time from x position, open popup
  const handleRowClick = useCallback((e, taskId) => {
    const rect      = e.currentTarget.getBoundingClientRect();
    const clickX    = e.clientX - rect.left;
    const rawMins   = TL_START + (clickX / hourW) * 60;
    const snapMins  = Math.min(15, intervalMins); // finer zoom → finer snapping
    const startMins = Math.round(rawMins / snapMins) * snapMins;
    const endMins   = Math.min(startMins + 60, TL_END);
    onOpenPopup(e, taskId, dateKey, minsToTime(Math.max(startMins, TL_START)), minsToTime(endMins));
  }, [dateKey, onOpenPopup, hourW, intervalMins]);

  // Sort: planned blocks first (by earliest start) → unplanned → done/cancelled last
  const sortedTasks = useMemo(() => {
    return [...visibleTasks].sort((a, b) => {
      const isDoneA = a.status === 'done' || a.status === 'cancelled';
      const isDoneB = b.status === 'done' || b.status === 'cancelled';
      if (isDoneA !== isDoneB) return isDoneA ? 1 : -1;
      const aBlocks = dayBlocks.filter(bl => bl.taskId === a.id);
      const bBlocks = dayBlocks.filter(bl => bl.taskId === b.id);
      if (aBlocks.length && !bBlocks.length) return -1;
      if (!aBlocks.length && bBlocks.length) return 1;
      if (aBlocks.length && bBlocks.length) {
        return Math.min(...aBlocks.map(bl => bl.startMinutes)) - Math.min(...bBlocks.map(bl => bl.startMinutes));
      }
      return 0;
    });
  }, [visibleTasks, dayBlocks]);

  const nowLeft   = ((nowMins - TL_START) / 60) * hourW;
  const totalRows = sortedTasks.length;

  return (
    <div className="gantt-htl-outer" ref={outerRef}>
      <div style={{ width: LABEL_W + TL_WIDTH, minWidth: '100%', position: 'relative' }}>

        {/* ── Header row: "Tasks" label + zoom + hour ticks ── */}
        <div className="gantt-htl-header">
          <div className="gantt-htl-task-th" style={{ width: LABEL_W }}>
            <span className="gantt-htl-task-th-label">Tasks</span>
            <div className="gantt-htl-zoom-btns">
              <button className="gantt-htl-zoom-btn" onClick={onZoomOut} disabled={!canZoomOut} title="Zoom out">−</button>
              <button className="gantt-htl-zoom-btn" onClick={onZoomIn}  disabled={!canZoomIn}  title="Zoom in">+</button>
            </div>
          </div>
          <div className="gantt-htl-time-row" style={{ width: TL_WIDTH }}>
            {axisTicks.map(m => (
              <div key={m} className="gantt-htl-hour-cell" style={{ width: (intervalMins / 60) * hourW }}>
                {minsToTime(m)}
              </div>
            ))}
          </div>
        </div>

        {/* ── Task rows ── */}
        {sortedTasks.length === 0 ? (
          <div className="gantt-empty-row">No tasks — adjust filters or create a task.</div>
        ) : sortedTasks.map(task => {
          const isDone     = task.status === 'done' || task.status === 'cancelled';
          const isMine     = !!me && task.assigned_to_user_id === me.id;
          const isBlockedForClick = isDone || !isMine;
          const dueLabel   = fmtDue(task.due_on);
          const taskBlocks = dayBlocks
            .filter(b => b.taskId === task.id)
            .sort((a, b) => a.startMinutes - b.startMinutes);

          return (
            <div key={task.id} className={`gantt-htl-row gantt-p-${task.priority}`} style={{ height: ROW_H }}>

              {/* Sticky left: task info */}
              <div
                className="gantt-htl-task-label"
                style={{ width: LABEL_W, minHeight: ROW_H }}
                onClick={() => navigate(`/apps/${appId}/tasks/${task.id}`)}
              >
                <span className="gantt-task-num">{task.task_number || `#${task.id}`}</span>
                <span className={`gantt-task-title${isDone ? ' is-done' : ''}`}>{task.title}</span>
                {dueLabel && (
                  <span className={`gantt-task-due${isDone ? ' is-done' : ''}`}>
                    {dueLabel}
                  </span>
                )}
              </div>

              {/* Timeline: click to schedule, blocks drawn absolutely */}
              <div
                className={`gantt-htl-timeline${isDone ? ' is-done-cell' : ''}`}
                style={{ width: TL_WIDTH, height: ROW_H, cursor: isBlockedForClick ? 'not-allowed' : undefined }}
                onClick={isBlockedForClick ? undefined : e => handleRowClick(e, task.id)}
                title={isDone ? 'Task is completed — cannot plan' : !isMine ? 'Only the assignee can plan this task' : 'Click to schedule a time slot'}
              >
                {isDone && <div className="gantt-cell-done-hint" title="Completed — no planning needed">⚠</div>}
                {axisTicks.map(m => (
                  <div key={m} className="gantt-htl-hour-line" style={{ left: ((m - TL_START) / 60) * hourW }} />
                ))}
                {subTicks.map(m => (
                  <div key={`s${m}`} className="gantt-htl-half-line" style={{ left: ((m - TL_START) / 60) * hourW }} />
                ))}
                {taskBlocks.map(block => {
                  if (block.endMinutes <= TL_START || block.startMinutes >= TL_END) return null;
                  const clampStart = Math.max(block.startMinutes, TL_START);
                  const clampEnd   = Math.min(block.endMinutes, TL_END);
                  const left  = ((clampStart - TL_START) / 60) * hourW;
                  const width = Math.max(6, ((clampEnd - clampStart) / 60) * hourW - 2);
                  return (
                    <div
                      key={block.id}
                      className={`gantt-htl-block gantt-tb-${task.priority}`}
                      style={{ left, width, cursor: isMine && !isDone ? 'pointer' : undefined }}
                      onClick={e => { e.stopPropagation(); if (isMine && !isDone) onEdit(e, block); }}
                      title={`${minsToTime(block.startMinutes)} – ${minsToTime(block.endMinutes)} · ${durLabel(block.endMinutes - block.startMinutes)}${isMine && !isDone ? ' · Click to edit' : ''}`}
                    >
                      {width > 44 && (
                        <span className="gantt-htl-block-label">
                          {minsToTime(block.startMinutes)}–{minsToTime(block.endMinutes)}
                        </span>
                      )}
                      {isMine && !isDone && (
                        <button
                          className="gantt-tb-edit"
                          onClick={e => { e.stopPropagation(); onEdit(e, block); }}
                          title="Edit slot"
                        >✎</button>
                      )}
                      {isMine && (
                        <button
                          className="gantt-tb-remove"
                          onClick={e => { e.stopPropagation(); onRemove(block.id); }}
                          title="Remove"
                        >×</button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function TaskGantt({ tasks, appId, members, me, statusFilter, search, assigneeUserId }) {
  const navigate = useNavigate();
  const api      = useApi();
  const popupRef = useRef(null);

  // Whose schedule to display: the selected assignee filter, or the viewer's own by default.
  const viewUserId  = assigneeUserId ? Number(assigneeUserId) : me?.id;
  const viewingSelf = !me || viewUserId === me.id;

  const [viewMode, setViewMode] = useState('week');
  const [offset,   setOffset]   = useState(0);
  const [popup,    setPopup]    = useState(null);
  const [zoomIdx,  setZoomIdx]  = useState(DEFAULT_ZOOM);

  const { hourW: effectiveHourW, intervalMins: effectiveIntervalMins } = ZOOM_PRESETS[zoomIdx];

  const today    = useMemo(() => { const t = new Date(); t.setHours(0,0,0,0); return t; }, []);
  const todayKey = useMemo(() => toDateKey(today), [today]);

  const { dates, rangeLabel, weeksGroups } = useMemo(() => {
    let arr = [], label = '', groups = [];

    if (viewMode === 'day') {
      const d  = addDays(today, offset);
      arr      = [d];
      const di = (d.getDay() + 6) % 7;
      label    = `${DAY_FULL[di]}, ${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
      groups   = [[d]];

    } else if (viewMode === 'week') {
      const start = addDays(getMondayOf(today), offset * 7);
      for (let i = 0; i < 7; i++) arr.push(addDays(start, i));
      const end = arr[6];
      label  = `${MONTHS[start.getMonth()]} ${start.getDate()} – ${MONTHS[end.getMonth()]} ${end.getDate()}, ${end.getFullYear()}`;
      groups = [arr];

    } else {
      const ref  = new Date(today.getFullYear(), today.getMonth() + offset, 1);
      const days = new Date(ref.getFullYear(), ref.getMonth() + 1, 0).getDate();
      for (let i = 0; i < days; i++) arr.push(new Date(ref.getFullYear(), ref.getMonth(), i + 1));
      label = `${MONTHS_FULL[ref.getMonth()]} ${ref.getFullYear()}`;
      let week = [];
      for (const d of arr) {
        week.push(d);
        if (week.length === 7) { groups.push(week); week = []; }
      }
      if (week.length) groups.push(week);
    }

    return { dates: arr, rangeLabel: label, weeksGroups: groups };
  }, [viewMode, offset, today]);

  const switchView = useCallback((mode) => { setViewMode(mode); setOffset(0); }, []);

  const storageKey = `task-planner-v2-${appId}`;
  const [planBlocks, setPlanBlocks] = useState(() => {
    // The local cache only ever holds the viewer's own blocks (the server is
    // the source of truth for anyone else's schedule), so only seed from it
    // when we're about to display our own planner.
    if (!viewingSelf) return [];
    try {
      const raw = JSON.parse(localStorage.getItem(storageKey) || '[]');
      return raw.filter(b => b.id && b.startMinutes != null && b.endMinutes != null);
    } catch { return []; }
  });

  // Pull blocks for whichever user's schedule is being viewed (self by
  // default, or the selected assignee — e.g. a manager checking a reportee's
  // planner) and re-fetch whenever that target changes.
  useEffect(() => {
    if (!viewUserId) { setPlanBlocks([]); return; }
    api.getPlannerBlocks(appId, { user_id: viewUserId }).then(serverBlocks => {
      const merged = (serverBlocks || []).filter(b => b.id && b.startMinutes != null && b.endMinutes != null);
      setPlanBlocks(merged);
      if (viewingSelf) localStorage.setItem(storageKey, JSON.stringify(merged));
    }).catch(() => { if (!viewingSelf) setPlanBlocks([]); });
  }, [appId, viewUserId]); // eslint-disable-line react-hooks/exhaustive-deps

  const savePlanBlocks = useCallback((blocks, changedDate) => {
    setPlanBlocks(blocks);
    if (viewingSelf) localStorage.setItem(storageKey, JSON.stringify(blocks));
    // Sync to server: send all blocks for the affected date
    // (the server always saves under the caller's own user id — scheduling
    // is only ever possible for tasks assigned to the viewer, see isMine)
    if (changedDate) {
      const dateBlocks = blocks.filter(b => b.date === changedDate);
      api.savePlannerBlocks(appId, changedDate, dateBlocks).catch(() => {});
    }
  }, [storageKey, api, appId, viewingSelf]);

  useEffect(() => {
    if (!popup) return;
    const handler = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target)) setPopup(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [popup]);

  const openPopup = useCallback((e, taskId, dateKey, startTime = '09:00', endTime = '10:00') => {
    e.stopPropagation();
    if (popup?.taskId === taskId && popup?.dateKey === dateKey) { setPopup(null); return; }
    // Anchor to the actual click point, not the (possibly off-screen) row/cell rect —
    // the timeline row can span the full 24h width and extend past the viewport.
    const anchorRect = { left: e.clientX, right: e.clientX, top: e.clientY, bottom: e.clientY };
    setPopup({ taskId, dateKey, startTime, endTime, anchorRect });
  }, [popup]);

  // Open the popup pre-filled with an existing block so its times can be edited
  const openEditPopup = useCallback((e, block) => {
    e.stopPropagation();
    const anchorRect = { left: e.clientX, right: e.clientX, top: e.clientY, bottom: e.clientY };
    setPopup({
      blockId:   block.id,
      taskId:    block.taskId,
      dateKey:   block.date,
      startTime: minsToTime(block.startMinutes),
      endTime:   minsToTime(Math.min(block.endMinutes, TL_END - 1)),
      anchorRect,
    });
  }, []);

  const popupOverlaps = useCallback((taskId, dateKey, start, end, ignoreId) => {
    return planBlocks.some(b =>
      b.id !== ignoreId &&
      b.taskId === taskId && b.date === dateKey &&
      start < b.endMinutes && end > b.startMinutes
    );
  }, [planBlocks]);

  const saveBlock = useCallback(() => {
    if (!popup) return;
    const start = timeToMins(popup.startTime);
    const end   = timeToMins(popup.endTime);
    if (end <= start) return;
    if (popupOverlaps(popup.taskId, popup.dateKey, start, end, popup.blockId)) return;
    const next = popup.blockId
      ? planBlocks.map(b => b.id === popup.blockId ? { ...b, startMinutes: start, endMinutes: end } : b)
      : [...planBlocks, { id: uid(), taskId: popup.taskId, date: popup.dateKey, startMinutes: start, endMinutes: end }];
    savePlanBlocks(next, popup.dateKey);
    setPopup(null);
  }, [popup, planBlocks, savePlanBlocks, popupOverlaps]);

  const removeBlock = useCallback((blockId) => {
    const block = planBlocks.find(b => b.id === blockId);
    const newBlocks = planBlocks.filter(b => b.id !== blockId);
    savePlanBlocks(newBlocks, block?.date);
    if (block?.id) api.deletePlannerBlock(appId, block.id).catch(() => {});
  }, [planBlocks, savePlanBlocks, api, appId]);

  // Quick-add: appends block after last existing block (or at current time/9am)
  const addQuickBlock = useCallback((taskId, durationMins) => {
    const dateKey  = toDateKey(dates[0]);
    const existing = planBlocks.filter(b => b.taskId === taskId && b.date === dateKey);
    let startMins;
    if (existing.length > 0) {
      startMins = Math.max(...existing.map(b => b.endMinutes));
    } else {
      const now = new Date();
      startMins = dateKey === todayKey
        ? Math.ceil((now.getHours() * 60 + now.getMinutes()) / 15) * 15
        : 540;
    }
    startMins = Math.max(startMins, TL_START);
    const endMins = Math.min(startMins + durationMins, TL_END);
    if (endMins <= startMins) return;
    savePlanBlocks([...planBlocks, { id: uid(), taskId, date: dateKey, startMinutes: startMins, endMinutes: endMins }], dateKey);
  }, [dates, planBlocks, savePlanBlocks, todayKey]);

  const visibleTasks = useMemo(() => {
    let result = tasks.filter(t => t.status !== 'done');
    if (statusFilter?.length) result = result.filter(t => statusFilter.includes(t.status));
    if (search?.trim()) {
      const q = search.toLowerCase();
      result = result.filter(t =>
        t.title.toLowerCase().includes(q) ||
        (t.task_number || '').toLowerCase().includes(q)
      );
    }
    return result;
  }, [tasks, statusFilter, search]);

  const totalMins = planBlocks.reduce((s, b) => s + (b.endMinutes - b.startMinutes), 0);

  const popupStyle = useMemo(() => {
    if (!popup?.anchorRect) return {};
    const r = popup.anchorRect;
    const pw = 280, ph = 240;
    let left = r.left;
    let top  = r.bottom + 6;
    if (left + pw > window.innerWidth - 8)  left = window.innerWidth - pw - 8;
    if (top  + ph > window.innerHeight - 8) top  = r.top - ph - 6;
    return { top, left };
  }, [popup]);

  const popupDur = popup ? timeToMins(popup.endTime) - timeToMins(popup.startTime) : 0;
  const popupHasOverlap = popup && popupDur > 0 &&
    popupOverlaps(popup.taskId, popup.dateKey, timeToMins(popup.startTime), timeToMins(popup.endTime), popup.blockId);
  const prevLabel = { day: '‹ Prev day', week: '‹ Prev week', month: '‹ Prev month' }[viewMode];
  const nextLabel = { day: 'Next day ›', week: 'Next week ›', month: 'Next month ›' }[viewMode];

  return (
    <div className="gantt-wrap">

      {/* ── Navigation bar ── */}
      <div className="gantt-nav">
        {/* Period navigation */}
        <div className="gantt-nav-btns">
          <button className="gantt-nav-btn" onClick={() => setOffset(o => o - 1)}>{prevLabel}</button>
          <button className="gantt-nav-btn gantt-nav-today" onClick={() => setOffset(0)}>Today</button>
          <button className="gantt-nav-btn" onClick={() => setOffset(o => o + 1)}>{nextLabel}</button>
        </div>

        <span className="gantt-range-label">{rangeLabel}</span>

        {/* Day / Week / Month toggle */}
        <div className="gantt-view-btns">
          {['day','week','month'].map(v => (
            <button
              key={v}
              className={`gantt-view-btn${viewMode === v ? ' active' : ''}`}
              onClick={() => switchView(v)}
            >
              {v.charAt(0).toUpperCase() + v.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* ── Day view: horizontal Gantt timeline ── */}
      {viewMode === 'day' && (
        <DayTimeline
          dateKey={toDateKey(dates[0])}
          todayKey={todayKey}
          dayBlocks={planBlocks.filter(b => b.date === toDateKey(dates[0]))}
          tasks={tasks}
          visibleTasks={visibleTasks}
          onRemove={removeBlock}
          onEdit={openEditPopup}
          onOpenPopup={openPopup}
          appId={appId}
          hourW={effectiveHourW}
          intervalMins={effectiveIntervalMins}
          onZoomIn={() => setZoomIdx(i => Math.min(ZOOM_PRESETS.length - 1, i + 1))}
          onZoomOut={() => setZoomIdx(i => Math.max(0, i - 1))}
          canZoomIn={zoomIdx < ZOOM_PRESETS.length - 1}
          canZoomOut={zoomIdx > 0}
          me={me}
        />
      )}

      {/* ── Week / Month: Gantt grid ── */}
      {viewMode !== 'day' && (
        <div className="gantt-scroll">
          <table
            className="gantt-table"
            data-view={viewMode}
            style={{}}
          >
            <thead>
              <tr>
                <th className="gantt-task-th" rowSpan={2}>Task</th>
                {weeksGroups.map((week, wi) => (
                  <th key={wi} colSpan={week.length} className="gantt-week-th">
                    {`${MONTHS[week[0].getMonth()]} ${week[0].getDate()}${week.length > 1 ? ` – ${week[week.length-1].getDate()}` : ''}`}
                  </th>
                ))}
              </tr>
              <tr>
                {dates.map((d, di) => {
                  const key       = toDateKey(d);
                  const isToday   = key === todayKey;
                  const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                  return (
                    <th key={di} className={`gantt-day-th${isToday ? ' is-today' : ''}${isWeekend ? ' is-weekend' : ''}`}>
                      <div className="gantt-day-num">{d.getDate()}</div>
                      <div className="gantt-day-name">{DAY_ABR[(d.getDay() + 6) % 7]}</div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {visibleTasks.length === 0 ? (
                <tr>
                  <td colSpan={dates.length + 1} className="gantt-empty-row">
                    No tasks to display — adjust filters or create a task.
                  </td>
                </tr>
              ) : visibleTasks.map((task) => {
                const dueKey     = task.due_on ? task.due_on.slice(0, 10) : null;
                const dueLabel   = fmtDue(task.due_on);
                const isDone     = task.status === 'done' || task.status === 'cancelled';
                const isMine     = !!me && task.assigned_to_user_id === me.id;
                const taskBlocks = planBlocks.filter(b => b.taskId === task.id);

                return (
                  <tr key={task.id} className={`gantt-row gantt-p-${task.priority}`}>
                    <td className="gantt-task-cell">
                      <div className="gantt-task-label" onClick={() => navigate(`/apps/${appId}/tasks/${task.id}`)}>
                        <span className="gantt-task-num">{task.task_number || `#${task.id}`}</span>
                        <span className={`gantt-task-title${isDone ? ' is-done' : ''}`}>
                          {task.title}
                        </span>
                        {dueLabel && (
                          <span className={`gantt-task-due${isDone ? ' is-done' : ''}`}>
                            {dueLabel}
                          </span>
                        )}
                      </div>
                    </td>

                    {dates.map((d) => {
                      const key       = toDateKey(d);
                      const isDue     = key === dueKey;
                      const isToday   = key === todayKey;
                      const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                      const isPast    = d < today;
                      const dayBlocks = taskBlocks
                        .filter(b => b.date === key)
                        .sort((a, b) => a.startMinutes - b.startMinutes);
                      const isActive  = popup?.taskId === task.id && popup?.dateKey === key;
                      const isBlockedForClick = isDone || !isMine;

                      return (
                        <td
                          key={key}
                          className={`gantt-cell${isToday ? ' is-today' : ''}${isWeekend ? ' is-weekend' : ''}${isPast ? ' is-past' : ''}${isActive ? ' is-active' : ''}${isDone ? ' is-done-cell' : ''}`}
                          onClick={isBlockedForClick ? undefined : (e) => openPopup(e, task.id, key)}
                          title={isDone ? 'Task is completed — cannot plan' : !isMine ? 'Only the assignee can plan this task' : 'Click to schedule a time slot'}
                          style={isBlockedForClick ? { cursor: 'not-allowed' } : undefined}
                        >
                          {isDue && <div className="gantt-due-pip" title={`Due: ${dueLabel}`} />}
                          {dayBlocks.map(block => (
                            <div
                              key={block.id}
                              className={`gantt-time-block gantt-tb-${task.priority}`}
                              style={isMine && !isDone ? { cursor: 'pointer' } : undefined}
                              title={isMine && !isDone ? 'Click to edit slot' : undefined}
                              onClick={isMine && !isDone ? (e => openEditPopup(e, block)) : undefined}
                            >
                              <span className="gantt-tb-time">
                                {minsToTime(block.startMinutes)}–{minsToTime(block.endMinutes)}
                              </span>
                              <span className="gantt-tb-dur">{durLabel(block.endMinutes - block.startMinutes)}</span>
                              {isMine && !isDone && (
                                <button
                                  className="gantt-tb-edit"
                                  onClick={e => openEditPopup(e, block)}
                                  title="Edit slot"
                                >✎</button>
                              )}
                              {isMine && (
                                <button
                                  className="gantt-tb-remove"
                                  onClick={e => { e.stopPropagation(); removeBlock(block.id); }}
                                  title="Remove slot"
                                >×</button>
                              )}
                            </div>
                          ))}
                          {dayBlocks.length === 0 && !isDue && !isDone && isMine && (
                            <div className="gantt-cell-hint">+</div>
                          )}
                          {isDone && (
                            <div className="gantt-cell-done-hint" title="Completed — no planning needed">⚠</div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Fixed popup — outside scroll container so it never clips */}
      {popup && (
        <div
          className="gantt-popup"
          ref={popupRef}
          style={{ position: 'fixed', zIndex: 1200, ...popupStyle }}
          onClick={e => e.stopPropagation()}
        >
          <div className="gantt-popup-title">{popup.blockId ? 'Edit time slot' : 'Schedule time slot'}</div>
          <div className="gantt-popup-fields">
            <div className="gantt-popup-field">
              <label className="gantt-popup-label">Start time</label>
              <input
                type="time"
                className="gantt-popup-input"
                value={popup.startTime}
                onChange={e => setPopup(p => ({ ...p, startTime: e.target.value }))}
                autoFocus
              />
            </div>
            <div className="gantt-popup-field">
              <label className="gantt-popup-label">End time</label>
              <input
                type="time"
                className="gantt-popup-input"
                value={popup.endTime}
                onChange={e => setPopup(p => ({ ...p, endTime: e.target.value }))}
              />
            </div>
          </div>
          <div className="gantt-popup-dur-row">
            {popupDur <= 0
              ? <span className="gantt-popup-err">End must be after start</span>
              : popupHasOverlap
                ? <span className="gantt-popup-err">Overlaps an existing planned slot for this task</span>
                : <span className="gantt-popup-dur">{durLabel(popupDur)}</span>
            }
          </div>
          <div className="gantt-popup-actions">
            {popup.blockId && (
              <button
                className="gantt-popup-cancel"
                style={{ color: 'var(--danger, #dc2626)', marginRight: 'auto' }}
                onClick={() => { removeBlock(popup.blockId); setPopup(null); }}
              >Delete</button>
            )}
            <button className="gantt-popup-cancel" onClick={() => setPopup(null)}>Cancel</button>
            <button className="gantt-popup-save" onClick={saveBlock} disabled={popupDur <= 0 || popupHasOverlap}>
              {popup.blockId ? 'Save' : 'Add slot'}
            </button>
          </div>
        </div>
      )}

      {visibleTasks.length > 0 && (
        <p className="gantt-task-count">
          {visibleTasks.length} task{visibleTasks.length !== 1 ? 's' : ''}
          {planBlocks.length > 0 && ` · ${planBlocks.length} slot${planBlocks.length !== 1 ? 's' : ''} · ${durLabel(totalMins)} planned`}
        </p>
      )}
    </div>
  );
}
